<template>
  <div class="quill-editor-wrap">
    <div ref="editor"></div>
  </div>
</template>

<script>
// Local Quill 2 editor replacing @ckeditor/ckeditor5-build-classic
// (CKEditor v42+ requires a license key; Quill 2 is BSD-3).
// Keeps the exact <ckeditor :editor v-model :config> call-site contract —
// the editor/config props are accepted for compatibility and ignored
// (every call site passed an empty config anyway).
import Quill from 'quill';
import 'quill/dist/quill.snow.css';

export default {
  name: 'QuillEditor',
  props: {
    modelValue: { type: String, default: '' },
    editor: { type: [Function, Object, null], default: null },   // compat, ignored
    config: { type: Object, default: () => ({}) },               // compat, ignored
    placeholder: { type: String, default: '' },
  },
  emits: ['update:modelValue'],
  data() {
    return { quill: null, lastEmitted: null };
  },
  watch: {
    modelValue(value) {
      if (this.quill && value !== this.lastEmitted) {
        // Route through Quill's Delta parser instead of assigning innerHTML:
        // innerHTML does not run <script>, but it DOES fire inline handlers
        // (<img src=x onerror=...>), and this content is authored by one staff
        // user and rendered to others. dangerouslyPasteHTML parses to a Delta
        // against the registered formats, dropping attributes/handlers.
        this.setHtml(value);
      }
    },
  },
  methods: {
    /**
     * Same sanitising path as dangerouslyPasteHTML (clipboard matchers ->
     * Delta), but without its setSelection() call, which throws
     * "Cannot read properties of null (reading 'offset')" when run from
     * mounted() before the editor has a laid-out selection.
     */
    setHtml(html) {
      const delta = this.quill.clipboard.convert({ html: html || '' });
      this.quill.setContents(delta, 'silent');
    },
  },
  mounted() {
    this.quill = new Quill(this.$refs.editor, {
      theme: 'snow',
      placeholder: this.placeholder,
      modules: {
        // Roughly CKEditor Classic's default toolbar.
        toolbar: [
          ['bold', 'italic', 'underline'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ header: [1, 2, 3, false] }],
          ['link'],
          ['clean'],
        ],
      },
    });
    if (this.modelValue) this.setHtml(this.modelValue);
    this.quill.on('text-change', () => {
      const html = this.quill.root.innerHTML;
      this.lastEmitted = html === '<p><br></p>' ? '' : html;
      this.$emit('update:modelValue', this.lastEmitted);
    });
  },
  beforeUnmount() {
    this.quill = null;
  },
};
</script>

<style>
.quill-editor-wrap .ql-container {
  min-height: 10rem;
  font-size: inherit;
  font-family: inherit;
  background: #fff;
}
.quill-editor-wrap .ql-toolbar {
  background: #fff;
}
</style>
